import { useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import ContentView from '~/components/ContentView';
import { useExperiences } from '~/hooks/api/useExperiences';
import moment from 'moment';
import Spacings from '~/modules/ui-kit/spacings';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import ContentNotFound from '~/components/ContentNotFound';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ThumbDownSharp from '@mui/icons-material/ThumbDownSharp';
import ThumbUpSharp from '@mui/icons-material/ThumbUpSharp';
import DrawerButton from '../DrawerButton';
import messages from './messages';
import { ExperienceDetailView } from './ExperienceDetailView';

const route = getRouteApi('/experienceList');

const ExperienceListPanel = styled(List)(({ theme }) => ({
  overflowY: 'auto',
  width: '100%',
  flexShrink: 0,
  borderRight: `1px solid ${theme.palette.grey[200]}`,
  [theme.breakpoints.up('md')]: {
    width: '300px',
  },
}));

const ExperienceList = () => {
  const { data, isLoading } = useExperiences();
  const navigate = useNavigate();
  const { experienceId } = route.useSearch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const selectedExperience = data?.find((exp) => exp.id === experienceId);
  const showList = isDesktop || !experienceId;
  const showDetail = isDesktop ? !!data?.length : !!experienceId;

  // Auto-select first experience on desktop when none selected
  useEffect(() => {
    if (isDesktop && data?.length && !experienceId) {
      navigate({
        to: '/experienceList',
        search: { experienceId: data[0].id },
        replace: true,
      });
    }
  }, [isDesktop, data, experienceId, navigate]);

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <DrawerButton />
              </Grid>
              <Grid>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      }
      isLoading={isLoading}
    >
      <Box sx={{ display: 'flex', height: '100%' }}>
        {showList && (
          <ExperienceListPanel>
            {data?.map((experience) => (
              <ListItemButton
                key={experience.id}
                component={Link}
                to={`/experienceList?experienceId=${experience.id}`}
                selected={experienceId === experience.id}
              >
                <ListItemText
                  primary={`${experience.publisher.firstName} ${experience.publisher.lastName}`}
                  slots={{
                    secondary: 'div',
                  }}
                  secondary={
                    <>
                      <div>
                        <Typography variant="caption">
                          {moment(experience.events[0].eventDate).format('l')}
                        </Typography>
                      </div>
                      <div>{experience.title}</div>
                    </>
                  }
                />
                {experience.isPositive ? (
                  <ThumbUpSharp sx={{ ml: 1, color: 'grey.500' }} />
                ) : (
                  <ThumbDownSharp sx={{ ml: 1, color: 'grey.500' }} />
                )}
              </ListItemButton>
            ))}
          </ExperienceListPanel>
        )}
        {showDetail && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Spacings.Inset>
              <Spacings.Stack scale="s">
                {!isDesktop && (
                  <Link
                    to="/experienceList"
                    search={{ experienceId: undefined }}
                  >
                    <Button variant="text" startIcon={<ArrowBack />}>
                      <FormattedMessage {...messages.backToExperienceList} />
                    </Button>
                  </Link>
                )}
                {selectedExperience ? (
                  <ExperienceDetailView experience={selectedExperience} />
                ) : (
                  <ContentNotFound />
                )}
              </Spacings.Stack>
            </Spacings.Inset>
          </Box>
        )}
      </Box>
    </ContentView>
  );
};

export default ExperienceList;
